import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {
    AirspaceCheckerParametersWithStartEnd,
    MapAirspaceCheckerService
} from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    ImageReference,
    LocationFeatureDto,
    LocationService,
    MapStyleService,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { getFileName, toMapFeature } from '@flyfreely-portal-ui/locations';
import { FeatureGroup, MapDataInput } from '@flyfreely-portal-ui/map';
import {
    MissionNoticesDataService,
    SimplifiedLotamDetails
} from '@flyfreely-portal-ui/notices-shared';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import FileSaver from 'file-saver';
import {
    DefaultKmlFeatureTypesByGeometryType,
    KmlFeatureGroups,
    KmlUploadService,
    UploadedFeature,
    isSinglePolygon
} from 'libs/locations/src/lib/kml-upload.service';
import { showKmlUploadDialogue } from 'libs/locations/src/lib/kml-upload/kml-upload-dialogue.component';
import { LocationEditDrawingComponent } from 'libs/locations/src/lib/location-edit-drawing/location-edit-drawing.component';
import { mergeFeatureCollectionWithSprites } from 'libs/map/src/lib/flyfreely-map/helpers';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { getNoticeFeatureGroups } from '../helpers';

@Component({
    selector: 'lotam-location-edit',
    templateUrl: './lotam-location-edit.component.html',
    styleUrls: ['./lotam-location-edit.component.scss'],
    providers: [
        MapAirspaceCheckerService,
        MissionNoticesDataService,
        SitesDataService,
        OperationsSearchDataService
    ]
})
export class LotamLocationEditComponent implements OnInit, OnDestroy {
    @Input() organisationId: number;
    /** Timezone of the managing organisation, used for LOTAM overlay time display. */
    @Input() organisationTimeZone: string;
    @Input() locationFeatures: LocationFeatureDto[] = [];
    @Input() lotamDetails: SimplifiedLotamDetails | null = null;
    @Input() isNewLocation: boolean;

    @Output() featuresChange = new EventEmitter<LocationFeatureDto[]>();

    featureGroups: FeatureGroup[] = [];

    @ViewChild('drawingComponent')
    drawingComponent: LocationEditDrawingComponent;

    private nextId = 1;

    mapData$ = new BehaviorSubject<MapDataInput>({});
    airspaceValue$ = new BehaviorSubject<AirspaceCheckerParametersWithStartEnd>(
        {
            airspaceDate: null,
            airspaceDuration: 0,
            airspaceHeight: 0,
            airspaceRuleset: null,
            startTime: null,
            endTime: null,
            airspaceTimeZone: null
        }
    );

    additionalImages?: ImageReference[];

    workTracker = new WorkTracker();

    isAddingFeature = false;
    hasAnyFeatures = false;
    private hasChanged = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<LotamLocationEditComponent>,
        private missionNoticesDataService: MissionNoticesDataService,
        private mapStylesService: MapStyleService,
        private commonDialoguesService: CommonDialoguesService,
        private locationService: LocationService,
        private kmlUploadService: KmlUploadService,
        private modalService: BsModalService,
        private logging: FlyFreelyLoggingService,
        private operationsSearchDataService: OperationsSearchDataService
    ) {}

    ngOnInit() {
        this.additionalImages = this.mapStylesService.generateImageReferences([
            'NOTICE-NO_FLY-FFA500',
            'NOTICE-DANGER-FFA500',
            'NOTICE-AREA_OF_INTEREST-FFA500'
        ]);

        const builtGroups = getNoticeFeatureGroups(
            this.locationFeatures,
            1,
            1,
            'FFA500'
        );
        this.featureGroups = builtGroups.features;
        this.nextId = builtGroups.nextId + 1;
        this.missionNoticesDataService.lotams$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(notices => {
                const filteredNotices =
                    this.lotamDetails != null
                        ? notices.filter(
                              n => n.id !== this.lotamDetails.lotamId
                          )
                        : notices;
                this.mapData$.next({
                    LOTAM: mergeFeatureCollectionWithSprites(
                        filteredNotices.map(n => n.displayLocation)
                    )
                });
            });

        if (this.lotamDetails != null) {
            this.operationsSearchDataService.find(
                this.lotamDetails.dateRange,
                this.organisationId
            );
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.mapData$.complete();
        this.airspaceValue$.complete();
    }

    onFeatureGroupsChange(featureGroups: FeatureGroup[]) {
        this.featureGroups = featureGroups;
        this.hasChanged = true;
    }

    onIsAddingFeatureChange(value: boolean) {
        this.isAddingFeature = value;
    }

    onHasAnyFeaturesChange(value: boolean) {
        this.hasAnyFeatures = value;
    }

    private getFeatures(): LocationFeatureDto[] {
        return (
            this.drawingComponent?.getFeatureGroups() ?? this.featureGroups
        ).reduce(
            (acc, fg) =>
                acc.concat(
                    fg.existingFeatures.reduce(
                        (acc2, f) =>
                            acc2.concat({
                                name: f.name,
                                geometry: f.geom,
                                type: f.categoryId,
                                properties: f.properties
                            }),
                        []
                    )
                ),
            []
        );
    }

    private addFeature(locationFeature: LocationFeatureDto) {
        if (
            !this.featureGroups.some(
                fg =>
                    fg.categories.findIndex(
                        c => c.id === locationFeature.type
                    ) !== -1
            )
        ) {
            this.logging.warn(
                `Feature '${locationFeature.name}' of type ${locationFeature.type} is not supported and was skipped`
            );
            return;
        }
        this.featureGroups = this.featureGroups.map(fg =>
            fg.categories.findIndex(c => c.id === locationFeature.type) !== -1
                ? {
                      ...fg,
                      existingFeatures: fg.existingFeatures.concat(
                          toMapFeature(locationFeature, this.nextId++)
                      )
                  }
                : fg
        );
        // Notify drawing component of the updated feature groups
        if (this.drawingComponent) {
            this.drawingComponent.internalFeatureGroups = this.featureGroups;
            this.drawingComponent.clearUndoHistory();
            this.drawingComponent.validateFeatures();
            this.drawingComponent.zoomToAllFeatures(false);
        }
    }

    // ---- KML / Upload ----

    uploadLocation(locationFiles: File) {
        const locationFile = locationFiles;

        if (
            locationFile.type === 'application/json' ||
            locationFile.name.toLowerCase().endsWith('.json')
        ) {
            this.logging.error(
                null,
                'The uploaded file was not of a valid KML/KMZ filetype.'
            );
            return;
        }

        this.locationService
            .uploadLocation(locationFile)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                take(1),
                tap({
                    next: DO_NOTHING,
                    error: (error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error uploading KML file: ${error.message}`
                        )
                })
            )
            .subscribe(featureCollection => {
                const defaultFeatureTypes: DefaultKmlFeatureTypesByGeometryType =
                    {
                        polygon: LocationFeatureDto.Type.AREA_OF_INTEREST,
                        point: LocationFeatureDto.Type.POINT_OF_INTEREST,
                        lineString: LocationFeatureDto.Type.FLIGHT_PATH_LINE
                    };

                // Parse uploaded features and filter out unsupported types.
                const features = this.kmlUploadService
                    .parseKmlGroupings(featureCollection, defaultFeatureTypes)
                    .filter(
                        feature =>
                            feature.featureType === 'Areas' ||
                            feature.featureType === 'Corridors' ||
                            feature.featureType === 'Lines'
                    );

                if (features.length === 0) {
                    this.logging.error(
                        null,
                        `The uploaded KML does not contain any valid location geometry for creating a LOTAM area definition.`
                    );
                } else if (isSinglePolygon(features)) {
                    this.parseSingleKmlFeature(
                        features[0].features[0],
                        locationFile.name
                    );
                } else {
                    showKmlUploadDialogue(
                        this.modalService,
                        features,
                        locationFile.name,
                        [
                            LocationFeatureDto.Type.FLIGHT_AREA,
                            LocationFeatureDto.Type.FLIGHT_PATH_LINE,
                            LocationFeatureDto.Type.GENERAL_LINE,
                            LocationFeatureDto.Type.OFFSET_FLIGHT_AREA
                        ]
                    )
                        .content.selectedFeatures.pipe(
                            takeUntil(this.ngUnsubscribe$)
                        )
                        .subscribe(features =>
                            this.parseComplexKml(features, locationFile.name)
                        );
                }
            })
            .add(this.workTracker.createTracker());
    }

    parseSingleKmlFeature(feature: UploadedFeature, fileName: string) {
        this.addFeature({
            name: fileName,
            type: feature.featureType,
            geometry: <GeoJSON.Polygon>feature.geometry
        } as LocationFeatureDto);
    }

    parseComplexKml(features: KmlFeatureGroups[], name: string) {
        features.forEach(group => {
            group.features.forEach(f => {
                this.addFeature({
                    name: f.name,
                    type: f.featureType,
                    geometry: f.geometry,
                    properties: f.properties
                } as LocationFeatureDto);
            });
        });
    }

    downloadKml() {
        const name = this.lotamDetails?.name ?? 'Location';
        this.locationService
            .downloadKmlFromFeatures({ name, features: this.getFeatures() })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: download => {
                    const headers = download.headers;
                    const fileName =
                        getFileName(headers.get('content-disposition')) ??
                        'flyfreely.kml';

                    FileSaver.saveAs(download.body, fileName);
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error downloading KML file: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    save() {
        this.featuresChange.emit(this.getFeatures());
        this.hasChanged = false;
        this.modal.hide();
    }

    close() {
        if (this.hasChanged) {
            this.commonDialoguesService
                .showConfirmationDialogue(
                    'Confirm Cancel',
                    'You have unsaved changes, are you sure you want to cancel?',
                    'Yes',
                    () => Promise.resolve()
                )
                // Resolves when the user confirms; rejects if user declines or
                // dialogue is dismissed — in both cases we do nothing.
                .then(() => this.modal.hide())
                .catch(() => undefined);
        } else {
            this.modal.hide();
        }
    }
}
